import { memo } from 'react';
import { Box, Tooltip } from '@mui/material';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';
import { QuestionType } from '@/components/campaigns/CampaignQuestionsEditor/types';
import { hasRatingScale } from '@/components/campaigns/CampaignQuestionsEditor/utils';
import QuestionTypeIcon from './QuestionTypeIcon';
import QuestionAnswer from './QuestionAnswer';
import QuestionTitle from './QuestionTitle';
import InsightFeedback from './InsightFeedback';
import type { InsightFeedback as InsightFeedbackValue } from '@/types/insights';
import type {
    CampaignQuestion,
    CampaignQuestionWithAnswer,
    NumericQuestion,
    QuestionAnswerType,
} from '@/components/campaigns/CampaignQuestionsEditor/types';
import type { QuestionAnswerProps } from './QuestionAnswer';

interface CallInsightItemProps {
    question: CampaignQuestionWithAnswer | null | undefined;
    allQuestions?: CampaignQuestion[];
    index?: number;
    visible?: boolean;
    prefilledFormula?: string | null;
    questionModified?: boolean;
    canRate?: boolean;
    callId?: number | null;
    insightQuestionId?: number | null;
    feedback?: InsightFeedbackValue | null;
}

// `false` and `0` are valid answers, so only missing or empty values are unanswered.
const hasAnswer = (answer: QuestionAnswerType): boolean => {
    if (answer === null || answer === undefined) {
        return false;
    }

    return Array.isArray(answer) ? answer.length > 0 : answer !== '';
};

/**
 * Converts a CampaignQuestionWithAnswer into its corresponding QuestionAnswerProps for rendering.
 */
const buildQuestionAnswerProps = (
    question: CampaignQuestionWithAnswer,
    allQuestions: CampaignQuestion[],
    prefilledFormula?: string | null
): QuestionAnswerProps => {
    const { type, answer } = question;

    switch (type) {
        case QuestionType.TEXT:
            return { type, answer: answer as string | null };
        case QuestionType.FORMULA:
            return {
                type,
                answer: answer as string | null,
                formula: question.formula,
                prefilledFormula,
                allQuestions,
            };
        case QuestionType.BOOLEAN:
            return { type, answer: answer as boolean | string | null };
        case QuestionType.NUMERIC:
            return {
                type,
                answer: !isNaN(Number(answer)) ? Number(answer) : null,
                maxScore: (question as NumericQuestion).maxValue,
            };
        case QuestionType.MULTISELECT:
            // Only use the answer if the server parsed it as an array (answer_type was multiselect).
            // A plain string means the answer was stored under a different question type (e.g. boolean).
            return {
                type,
                answer: Array.isArray(answer) ? (answer as string[]) : null,
            };
    }
};

const CallInsightItem: React.FC<CallInsightItemProps> = ({
    question,
    allQuestions = [],
    index = 0,
    visible = true,
    prefilledFormula = null,
    questionModified = false,
    canRate = false,
    callId = null,
    insightQuestionId = null,
    feedback = null,
}) => {
    if (!question) return null;

    return (
        <Box
            sx={{
                display: 'flex',
                flexDirection: 'column',
                width: '100%',
                backgroundColor: 'devblue.10',
                borderRadius: 2,
                gap: 0.5,
                px: 0.25,
                py: 1,
                opacity: visible ? 1 : 0,
                transform: visible ? 'translateY(0)' : 'translateY(10px)',
                transition: 'opacity 0.3s ease-out, transform 0.3s ease-out',
            }}
        >
            <Box sx={{ flex: 2, display: 'flex', mx: 1, minWidth: 0 }}>
                <Box mr={1} mt={0.1}>
                    <QuestionTypeIcon
                        type={question.type}
                        useRatingScale={hasRatingScale(question)}
                        iconStyle={{ fontSize: '16px', color: 'secondary.main' }}
                    />
                </Box>
                <Box sx={{ width: '100%', minWidth: 0 }}>
                    <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 0.5 }}>
                        <Box sx={{ flex: 1, minWidth: 0 }}>
                            <QuestionTitle question={question} index={index} />
                        </Box>
                        {questionModified && (
                            <Tooltip
                                title="Question was modified after this insight was generated. Regenerate for the latest version."
                                placement="top"
                            >
                                <WarningAmberIcon sx={{ fontSize: '16px', color: 'warning.main', mt: 0.25, flexShrink: 0 }} />
                            </Tooltip>
                        )}
                    </Box>
                    <QuestionAnswer {...buildQuestionAnswerProps(question, allQuestions, prefilledFormula)} />

                    {/* Hide feedback for modified questions because the answer was generated using an older version of the question. */}
                    {canRate && callId && insightQuestionId && !questionModified && hasAnswer(question.answer) && (
                        <InsightFeedback callId={callId} questionId={insightQuestionId} feedback={feedback} />
                    )}
                </Box>
            </Box>
        </Box>
    );
};

export default memo(CallInsightItem);
